import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircleIcon,
  PlayCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { agentService, AgentCampaign } from '../../services/agentService';
import { leadService } from '../../services/leadService';
import { isViewResultsDialableCampaign, useDialLead } from '../../hooks/useDialLead';
import { useWebRTC } from '../../contexts/WebRTCContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LeadDetailSidebar from '../../components/Leads/LeadDetailSidebar';
import LeadResultCard from '../../components/Leads/LeadResultCard';
import FollowUpDateRangeFilter, { FollowUpDateRange } from '../../components/Leads/FollowUpDateRangeFilter';
import { LeadStatus, LeadSubStage } from '../../types/lead';
import toast from 'react-hot-toast';

type StatusFilter = 'all' | 'interested' | 'follow_up' | 'app_in' | 'app_out';

const PAGE_SIZE = 25;

interface AgentLead {
  id: string;
  full_name: string | null;
  phone_e164: string | null;
  email: string | null;
  company_name: string | null;
  status: string;
  sub_stage_id?: string | null;
  sub_stage?: LeadSubStage | null;
  sub_stage_name?: string | null;
  assigned_agent?: { id: string; name: string } | null;
  tagged_agents?: Array<{ id: string; name: string }>;
  custom?: Record<string, any>;
  campaign_name: string | null;
  campaign_id: string | null;
  campaign_type?: string | null;
  dialing_type?: string | null;
  latest_call_status: string | null;
  latest_call_outcome?: string | null;
  latest_reason: string | null;
  notes: string | null;
  follow_up_date: string | null;
  dial_count: number | null;
  last_call_at: string | null;
  created_at: string;
  updated_at: string;
}

type DatePreset = 'today' | 'yesterday' | 'all' | 'custom';

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getPresetRange(preset: 'today' | 'yesterday'): { from: string; to: string } {
  const now = new Date();
  if (preset === 'today') {
    const d = toLocalDateString(now);
    return { from: d, to: d };
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const d = toLocalDateString(yesterday);
  return { from: d, to: d };
}

const formatDateLabel = (range: { from: string; to: string }) => {
  const fmt = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  if (range.from === range.to) return fmt(range.from);
  return `${fmt(range.from)} – ${fmt(range.to)}`;
};

const STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'app_out', label: 'App Out' },
  { value: 'app_in', label: 'App In' },
  { value: 'unqualified', label: 'Unqualified' },
];

const AgentResultsPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [subStageFilter, setSubStageFilter] = useState<string>('');
  const [campaignFilter, setCampaignFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [followUpDateRange, setFollowUpDateRange] = useState<FollowUpDateRange | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [dateRange, setDateRange] = useState<{ from: string; to: string } | null>(null);
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const customAnchorRef = useRef<HTMLButtonElement>(null);
  const customPanelRef = useRef<HTMLDivElement>(null);
  const [campaigns, setCampaigns] = useState<AgentCampaign[]>([]);
  const [subStages, setSubStages] = useState<LeadSubStage[]>([]);
  const [leads, setLeads] = useState<AgentLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [subStageCounts, setSubStageCounts] = useState<Record<string, number>>({});
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [updatingLeadId, setUpdatingLeadId] = useState<string | null>(null);
  const [subStageCache, setSubStageCache] = useState<Record<string, LeadSubStage[]>>({});
  const loadedSubStageKeys = useRef(new Set<string>());
  const { dialLead, isDialing } = useDialLead();
  const { setOnDispositionComplete } = useWebRTC();

  useEffect(() => {
    const pipelineStatus = statusFilter === 'all' ? 'pipeline' : statusFilter;
    agentService.getMyCampaigns(pipelineStatus).then(setCampaigns).catch(() => {});
  }, [statusFilter]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const activeMainStatus = statusFilter === 'all' ? undefined : statusFilter;

  // Sub-stage chips appear as soon as a status tab is picked. Scoped to the selected
  // campaign when one is chosen, otherwise merged across the agent's assigned campaigns.
  useEffect(() => {
    const loadSubStages = async () => {
      if (!activeMainStatus) {
        setSubStages([]);
        setSubStageFilter('');
        return;
      }
      try {
        const stages = await leadService.getSubStages({
          campaignId: campaignFilter || undefined,
          mainStatus: activeMainStatus,
        });
        setSubStages(stages);
      } catch {
        setSubStages([]);
      }
    };
    void loadSubStages();
  }, [campaignFilter, activeMainStatus]);

  useEffect(() => {
    if (!customDateOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (customAnchorRef.current?.contains(target) || customPanelRef.current?.contains(target)) return;
      setCustomDateOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [customDateOpen]);

  const handleDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    setCustomDateOpen(false);
    if (preset === 'all') {
      setDateRange(null);
    } else if (preset === 'today' || preset === 'yesterday') {
      setDateRange(getPresetRange(preset));
    } else if (preset === 'custom') {
      setDraftFrom(dateRange?.from ?? '');
      setDraftTo(dateRange?.to ?? '');
      setCustomDateOpen(true);
    }
    setCurrentPage(1);
  };

  const handleCustomApply = () => {
    if (!draftFrom || !draftTo || draftFrom > draftTo) return;
    setDateRange({ from: draftFrom, to: draftTo });
    setDatePreset('custom');
    setCustomDateOpen(false);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    fetchLeads(currentPage);
  };

  const filteredCampaigns = useMemo(() => campaigns, [campaigns]);

  const fetchLeads = useCallback(async (page = 1, opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true);
      let apiStatus: string | undefined;
      if (followUpDateRange) {
        apiStatus = 'follow_up';
      } else if (statusFilter === 'all') {
        apiStatus = 'interested,follow_up,app_in,app_out';
      } else {
        apiStatus = statusFilter;
      }

      const data = await agentService.getMyResults({
        status: apiStatus,
        page,
        limit: PAGE_SIZE,
        campaignId: campaignFilter || undefined,
        search: debouncedSearch || undefined,
        subStageId: subStageFilter || undefined,
        followUpDateFrom: followUpDateRange?.from,
        followUpDateTo: followUpDateRange?.to,
        dateFrom: dateRange?.from,
        dateTo: dateRange?.to,
        sortBy: 'statusChangedAt',
        sortOrder,
        assignedOnly: true,
      });

      setLeads(data.leads);
      const effectiveTotal = followUpDateRange
        ? data.pagination.total
        : statusFilter === 'all'
        ? (data.tabCounts?.interested || 0) + (data.tabCounts?.follow_up || 0) + (data.tabCounts?.app_in || 0) + (data.tabCounts?.app_out || 0)
        : data.pagination.total;
      setTotalCount(effectiveTotal);
      setTotalPages(Math.max(1, Math.ceil(effectiveTotal / PAGE_SIZE)));
      setCurrentPage(data.pagination.page);
      if (data.tabCounts) setTabCounts(data.tabCounts);
      setSubStageCounts(data.subStageCounts || {});
    } catch (error) {
      console.error('Failed to fetch results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, campaignFilter, debouncedSearch, subStageFilter, followUpDateRange, dateRange, sortOrder]);

  useEffect(() => {
    setOnDispositionComplete(() => {
      void fetchLeads(currentPage, { silent: true });
    });
    return () => setOnDispositionComplete(null);
  }, [currentPage, fetchLeads, setOnDispositionComplete]);

  useEffect(() => {
    setCurrentPage(1);
    fetchLeads(1);
  }, [fetchLeads]);

  // Load sub-stage dropdown options once per campaign (all statuses in one request),
  // then group by main status — avoids a request per (campaign × status) combination.
  useEffect(() => {
    const pendingCampaigns = new Set<string>();
    for (const lead of leads) {
      if (lead.campaign_id && !loadedSubStageKeys.current.has(lead.campaign_id)) {
        pendingCampaigns.add(lead.campaign_id);
      }
    }
    for (const cid of pendingCampaigns) {
      loadedSubStageKeys.current.add(cid);
      leadService.getSubStages({ campaignId: cid })
        .then((stages) => setSubStageCache((prev) => {
          const next = { ...prev };
          const byStatus: Record<string, LeadSubStage[]> = {};
          for (const s of stages) (byStatus[s.mainStatus] ??= []).push(s);
          for (const [st, list] of Object.entries(byStatus)) next[`${cid}:${st}`] = list;
          return next;
        }))
        .catch(() => undefined);
    }
  }, [leads]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    fetchLeads(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLeadFieldUpdate = async (
    lead: AgentLead,
    patch: { status?: LeadStatus; subStageId?: string | null },
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    e.stopPropagation();
    setUpdatingLeadId(lead.id);
    try {
      await leadService.updateLead(lead.id, patch);
      if (
        patch.status &&
        (['interested', 'follow_up', 'app_out', 'app_in'] as const).includes(
          patch.status as 'interested' | 'follow_up' | 'app_out' | 'app_in'
        )
      ) {
        setStatusFilter(patch.status as StatusFilter);
        setCurrentPage(1);
        await fetchLeads(1, { silent: true });
      } else {
        await fetchLeads(currentPage, { silent: true });
      }
    } catch {
      toast.error('Failed to update lead');
    } finally {
      setUpdatingLeadId(null);
    }
  };

  const interestedCount = tabCounts.interested || 0;
  const followUpCount = tabCounts.follow_up || 0;
  const appInCount = tabCounts.app_in || 0;
  const appOutCount = tabCounts.app_out || 0;
  const allCount = interestedCount + followUpCount + appInCount + appOutCount;

  const statusTabs: { id: StatusFilter; label: string; count: number; icon: React.ComponentType<{ className?: string }>; color: string; activeColor: string }[] = [
    { id: 'all', label: 'All Results', count: allCount, icon: FunnelIcon, color: 'text-muted-foreground', activeColor: 'bg-primary text-primary-foreground' },
    { id: 'interested', label: 'Interested', count: interestedCount, icon: CheckCircleIcon, color: 'text-green-600 dark:text-green-400', activeColor: 'bg-green-600 text-white dark:bg-green-500' },
    { id: 'follow_up', label: 'Follow Up', count: followUpCount, icon: PlayCircleIcon, color: 'text-blue-600 dark:text-blue-400', activeColor: 'bg-blue-600 text-white dark:bg-blue-500' },
    { id: 'app_out', label: 'App Out', count: appOutCount, icon: ClockIcon, color: 'text-indigo-600 dark:text-indigo-400', activeColor: 'bg-indigo-600 text-white dark:bg-indigo-500' },
    { id: 'app_in', label: 'App In', count: appInCount, icon: ClockIcon, color: 'text-sky-600 dark:text-sky-400', activeColor: 'bg-sky-600 text-white dark:bg-sky-500' },
  ];

  const canDialLead = (lead: AgentLead) =>
    Boolean(lead.campaign_id) &&
    isViewResultsDialableCampaign({
      campaignType: lead.campaign_type ?? undefined,
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">View Results</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pipeline results from your assigned campaigns — click a lead for full timeline, notes, and call history.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-4 w-4 text-muted-foreground" />
          <select
            value={campaignFilter}
            onChange={(e) => { setCampaignFilter(e.target.value); setCurrentPage(1); setSubStageFilter(''); }}
            className="input max-w-[280px] text-sm"
          >
            <option value="">All Campaigns</option>
            {filteredCampaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name, phone, company..."
            className="input w-full pl-9 text-sm"
          />
        </div>
        <select
          value={sortOrder}
          onChange={(e) => { setSortOrder(e.target.value as 'desc' | 'asc'); setCurrentPage(1); }}
          className="input max-w-[160px] text-sm"
          title="Sort by status-update date"
        >
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center bg-muted rounded-lg p-1 gap-1">
          {([
            { id: 'today' as DatePreset, label: 'Today' },
            { id: 'yesterday' as DatePreset, label: 'Yesterday' },
            { id: 'all' as DatePreset, label: 'All Time' },
          ]).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleDatePreset(opt.id)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                datePreset === opt.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <button
            ref={customAnchorRef}
            type="button"
            onClick={() => {
              if (datePreset === 'custom' && !customDateOpen) {
                setDraftFrom(dateRange?.from ?? '');
                setDraftTo(dateRange?.to ?? '');
                setCustomDateOpen(true);
              } else {
                handleDatePreset('custom');
              }
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              datePreset === 'custom'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            <CalendarDaysIcon className="h-4 w-4" />
            {datePreset === 'custom' && dateRange ? formatDateLabel(dateRange) : 'Custom'}
          </button>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          title="Refresh results"
          className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {customDateOpen &&
          createPortal(
            <div
              ref={customPanelRef}
              className="fixed z-[9998] w-[min(100vw-2rem,20rem)] rounded-xl border border-border bg-card p-4 shadow-xl"
              style={{
                top: (customAnchorRef.current?.getBoundingClientRect().bottom ?? 0) + 8,
                left: Math.min(
                  customAnchorRef.current?.getBoundingClientRect().left ?? 8,
                  window.innerWidth - 320 - 8
                ),
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">Custom date range</p>
                <button type="button" onClick={() => setCustomDateOpen(false)} className="rounded p-1 text-muted-foreground hover:bg-muted">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Show leads with a last call within this range.
              </p>
              <div className="space-y-3">
                <label className="block text-xs font-medium text-muted-foreground">
                  From
                  <input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
                </label>
                <label className="block text-xs font-medium text-muted-foreground">
                  To
                  <input type="date" value={draftTo} min={draftFrom || undefined} onChange={(e) => setDraftTo(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
                </label>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button size="sm" onClick={handleCustomApply} disabled={!draftFrom || !draftTo || draftFrom > draftTo}>
                  Apply
                </Button>
              </div>
            </div>,
            document.body
          )}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center bg-muted rounded-lg p-1 gap-1 flex-wrap">
          {statusTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = statusFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setStatusFilter(tab.id); setCurrentPage(1); setSubStageFilter(''); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  isActive ? tab.activeColor + ' shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-muted-foreground/10'}`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
          <FollowUpDateRangeFilter
            value={followUpDateRange}
            onChange={(range) => {
              setFollowUpDateRange(range);
              if (range) setStatusFilter('follow_up');
              setCurrentPage(1);
              setSubStageFilter('');
            }}
          />
        </div>
      </div>

      {subStages.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { setSubStageFilter(''); setCurrentPage(1); }}
            className={`text-xs px-3 py-1 rounded-full border ${!subStageFilter ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}
          >
            All sub-stages
          </button>
          {subStages.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => { setSubStageFilter(s.id); setCurrentPage(1); }}
              className={`text-xs px-3 py-1 rounded-full border ${subStageFilter === s.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}
            >
              {s.name} ({subStageCounts[s.id] || 0})
            </button>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Results
              <span className="ml-2 text-base font-normal text-muted-foreground">({totalCount.toLocaleString()})</span>
            </CardTitle>
            {totalPages > 1 && (
              <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <FunnelIcon className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground font-medium">No leads found</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {leads.map((lead) => (
                  <LeadResultCard
                    key={lead.id}
                    lead={lead}
                    statusOptions={STATUS_OPTIONS}
                    subStageOptions={subStageCache[`${lead.campaign_id}:${lead.status}`] || []}
                    updating={updatingLeadId === lead.id}
                    showCampaign
                    enableContactActions
                    dialing={isDialing(lead.id)}
                    onDial={
                      lead.campaign_id && canDialLead(lead)
                        ? (e) => {
                            e.stopPropagation();
                            void dialLead(lead.id, lead.campaign_id!, lead.full_name, {
                              resultsCallback: true,
                            });
                          }
                        : undefined
                    }
                    onLeadFieldUpdated={() => fetchLeads(currentPage, { silent: true })}
                    onViewDetails={() => setSelectedLeadId(lead.id)}
                    onStatusChange={(status, e) => handleLeadFieldUpdate(lead, { status }, e)}
                    onSubStageChange={(subStageId, e) => handleLeadFieldUpdate(lead, { subStageId }, e)}
                    onTagsUpdated={() => fetchLeads(currentPage, { silent: true })}
                    onFollowUpUpdated={() => fetchLeads(currentPage, { silent: true })}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button variant="outlined" size="sm" disabled={currentPage <= 1} onClick={() => handlePageChange(currentPage - 1)}>
                      <ChevronLeftIcon className="h-4 w-4 mr-1" /> Previous
                    </Button>
                    <Button variant="outlined" size="sm" disabled={currentPage >= totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                      Next <ChevronRightIcon className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <LeadDetailSidebar
        leadId={selectedLeadId}
        enableContactActions
        onClose={() => setSelectedLeadId(null)}
        onLeadUpdated={() => fetchLeads(currentPage, { silent: true })}
      />
    </div>
  );
};

export default AgentResultsPage;
